import type { Component } from 'solid-js'
import { createResource, createSignal, Show, createMemo } from 'solid-js'
import {
  getDateRangesForSimplePeriod,
  getDateRangesForCustomPeriod,
  formatDateToApiString,
  type SimplePeriodType,
  type CompareMode
} from '../../shared/helpers/date.functions'
import { useCorisEngagement } from '../../shared/providers/coris/coris-engagement.provider'
import { useUser } from '../../shared/providers/user-provider'
import { usePeriods } from '../../shared/providers/period.provider'
import PeriodSelector from '../shared/components/PeriodSelector'
import SummaryCard from '../shared/components/SummaryCard'
import VideosChart from '../shared/components/VideosChart'
import VideosTable from '../shared/components/VideosTable'
import EmptyState from '../shared/components/EmptyState'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonSummaryCard, SkeletonChart } from '../shared/components/Skeleton'
import type { SortByEntity, SortOrderEntity } from '../../features/engagement/data/entities/media.entity'

// Prevent tree-shaking of directive
sticky

const PAGE_SIZE = 15

const Videos: Component = () => {
  const { engagementPeriod, setEngagementPeriod } = usePeriods()
  const [currentPage, setCurrentPage] = createSignal(1)
  const [sortBy, setSortBy] = createSignal<SortByEntity>('times_played')
  const [sortOrder, setSortOrder] = createSignal<SortOrderEntity>('desc')
  const [isStuck, setIsStuck] = createSignal(false)

  const { getVideos } = useCorisEngagement()
  const { organizationSlug } = useUser()

  const [videosData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return {
        ...engagementPeriod(),
        page: currentPage(),
        sortBy: sortBy(),
        sortOrder: sortOrder(),
        organization
      }
    },
    async (params) => {
      if (!params) return null

      const dateRanges =
        params.selectedPeriod === 'custom' && params.customDateRange
          ? getDateRangesForCustomPeriod(params.customDateRange.from, params.customDateRange.to)
          : getDateRangesForSimplePeriod(params.selectedPeriod)

      let fromPrevious = dateRanges.fromPrevious
      let toPrevious = dateRanges.toPrevious

      if (params.compareMode === 'custom_range' && params.compareCustomRange) {
        fromPrevious = formatDateToApiString(params.compareCustomRange.from)
        toPrevious = formatDateToApiString(params.compareCustomRange.to)
      }

      return await getVideos.execute({
        fromDate: dateRanges.fromDate,
        toDate: dateRanges.toDate,
        fromPrevious,
        toPrevious,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        page: params.page,
        pageSize: PAGE_SIZE,
        organization: params.organization
      })
    }
  )

  const isEmpty = createMemo(() => {
    const data = videosData()
    return data && data.videos.length === 0 && data.summary.totalTimesViewed === 0 && data.summary.uniqueViewers === 0
  })

  const handlePeriodChange = (period: SimplePeriodType) => {
    setEngagementPeriod({ selectedPeriod: period })
    setCurrentPage(1) // Reset to first page when period changes
  }

  const handleCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ customDateRange: { from, to } })
    setCurrentPage(1) // Reset to first page when date range changes
  }

  const handleCompareModeChange = (mode: CompareMode) => {
    setEngagementPeriod({ compareMode: mode })
  }

  const handleCompareCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ compareCustomRange: { from, to } })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setSortBy(column as SortByEntity)
    setSortOrder(direction)
    setCurrentPage(1) // Reset to first page when sort changes
  }

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 -mx-6 px-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PeriodSelector
            selectedPeriod={engagementPeriod().selectedPeriod}
            onPeriodChange={handlePeriodChange}
            customDateRange={engagementPeriod().customDateRange}
            onCustomRangeChange={handleCustomRangeChange}
            compareMode={engagementPeriod().compareMode}
            onCompareModeChange={handleCompareModeChange}
            compareCustomRange={engagementPeriod().compareCustomRange}
            onCompareCustomRangeChange={handleCompareCustomRangeChange}
          />
        </div>
      </div>

      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8">
        <Show when={!videosData.loading && videosData() && isEmpty()}>
          <EmptyState />
        </Show>

        <Show when={!isEmpty()}>
          <Show
            when={videosData()?.summary}
            fallback={
              <div class="flex flex-col gap-4 sm:flex-row">
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
              </div>
            }
          >
            <div class="flex flex-col gap-4 sm:flex-row">
              <SummaryCard
                title="ls_videos_total_times_viewed"
                value={videosData()!.summary.totalTimesViewed}
                change={videosData()!.summary.totalTimesViewedChange}
              />
              <SummaryCard
                title="ls_videos_unique_viewers"
                value={videosData()!.summary.uniqueViewers}
                change={videosData()!.summary.uniqueViewersChange}
              />
            </div>
          </Show>

          <Show when={videosData()?.timeSeries} fallback={<SkeletonChart legendItems={2} />}>
            <VideosChart data={videosData()!.timeSeries} showTimesViewed={true} showUniqueViewers={true} />
          </Show>

          <VideosTable
            videos={videosData()?.videos ?? []}
            totalVideos={videosData()?.totalVideos ?? 0}
            page={currentPage}
            pageSize={PAGE_SIZE}
            onPageChange={handlePageChange}
            onSort={handleSort}
            sortColumn={sortBy}
            sortDirection={sortOrder}
            isLoading={videosData.state === 'unresolved' || videosData.loading}
          />
        </Show>
      </div>
    </div>
  )
}

export default Videos
